/** @jsx jsx */
import { jsx, keyframes } from '@emotion/react'
import { Link } from 'gatsby'
import { format, formatDistance, differenceInDays } from 'date-fns'
import Helmet from 'react-helmet'
import SanityImage from 'gatsby-plugin-sanity-image'
import PortableText from './portableText'
import tw, { theme, css } from 'twin.macro'
// Import a function to build the blog URL
import {imageUrlFor} from '../lib/image-url'
import { toPlainText, buildImageObj, getBlogUrl, getCategoryUrl } from '../lib/helpers'
import Tilt from 'react-parallax-tilt'
import { TagIcon } from '@heroicons/react/solid'
import { GatsbyImage } from 'gatsby-plugin-image'



function BlogPost(props) {
  const { _rawBody, categories, title, mainImage, publishedAt, _updatedAt, next, previous } = props

  const schemaOrgBlogPosting = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    'headline': title,
    'date': publishedAt,
    'dateModified': _updatedAt,
    'image': imageUrlFor(buildImageObj(mainImage)).url()
  }

  const blink = keyframes`
  from,
    to {
      color: transparent;
    }
    50% {
      color: var(--color-primary);
    }
  `

  const breakpoints = [640, 768, 1024]

  const mq = breakpoints.map(bp => `@media (min-width: ${bp}px)`)
  const maxMq = breakpoints.map(bp => `@media (max-width: ${bp}px)`)

  return (
    <article tw="mt-5 pt-5 md:mt-16 md:pt-20 mb-24 md:mb-32">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(schemaOrgBlogPosting)}</script>
      </Helmet>
      <div
        css={{
          '.notes > p:last-child:after': {
            content: '"|"',
            fontWeight: 600,
            animation: `0.8s ${blink} step-end infinite`,
          },
          '.gatsby-highlight': {
            '& pre': {
              borderRadius: 0,
              backgroundColor: 'transparent',
              margin: '0 auto',
              gridColumn: '1/-1',
              maxWidth: 'min(1024px, calc(100% - 48px))',
              paddingLeft: 0,
              paddingRight: 0,
              boxShadow: 'none',
              '& code': {
                color: `${theme`textColor.primary`}`,
              },
            },
          },
          '@media (min-width: 1024px)': {
            '& > aside': {
              gridColumn: 3,
              padding: '1.75rem',
              fontSize: '0.875rem',
              lineHeight: '1.25rem',
              maxWidth: '400px',
            },
          },
        }}
      >
        <div
          tw="grid gap-y-2"
          css={{
            maxWidth: 'min(1024px, calc(100% - 48px))',
            margin: '0 auto',
          }}
        >
          <h1
            tw="
        text-4xl lg:text-5xl md:text-center font-serif font-semibold"
          >
            {title}
          </h1>
          {publishedAt && (
            <div tw="md:text-center text-sm text-softer">
              {differenceInDays(new Date(publishedAt), new Date()) > 3
                ? formatDistance(new Date(publishedAt), new Date())
                : 'Published on ' + format(new Date(publishedAt), 'do MMM yyyy')}
            </div>
          )}
          {categories && (
            <div tw="flex flex-row flex-wrap justify-self-start md:justify-self-center mt-4">
              {categories.map(category => (
                <Link tw="mr-2" to={getCategoryUrl(category.slug)}>
                  <div
                    tw="inline-block text-xs font-sans font-medium py-1.5 px-3 bg-secondary text-softer leading-none align-middle border border-accent rounded-full"
                    key={category._id}
                  >
                    <TagIcon
                      tw="inline-block mr-1.5"
                      css={{
                        width: 10,
                        height: 10,
                      }}
                    />
                    {category.title}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>

        {mainImage && mainImage.asset && (
          <Tilt
            tiltReverse={true}
            tiltMaxAngleX="3.0"
            tiltMaxAngleY="3.0"
            gyroscope={true}
            glareMaxOpacity="0.2"
            glareReverse={true}
            glareEnable={true}
            css={{
              width: 'min(1024px, calc(100%))',
              margin: 'auto',
              gridColumn: '1 / -1',
            }}
            tw="mb-8 mt-8"
          >
            <figure
              css={{
                position: 'relative',
                overflow: 'hidden',
              }}
              tw="md:rounded md:shadow"
            >
              <div
                style={{
                  paddingBottom: '56.25%',
                }}
              ></div>
              <SanityImage
                {...mainImage}
                config={{ fit: 'max' }}
                css={{
                  '&[data-lqip]': {
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                  },
                }}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
              />
              <figcaption>{props.mainImage.caption}</figcaption>
            </figure>
          </Tilt>
        )}
        {_rawBody && (
          <div
            tw="font-serif prose prose-lg xl:prose-xl max-w-none"
            css={{
              fontFamily: `${theme`fontFamily.serif`}`,
            }}
          >
            <PortableText blocks={_rawBody} />
            {_updatedAt && (
              <div
                tw="mx-auto mt-16 text-softer font-sans text-sm flex flex-col sm:flex-row justify-start sm:items-center gap-y-4 sm:gap-y-0 sm:gap-x-1"
                css={{
                  maxWidth: 'min(650px, calc(100% - 48px))',
                }}
              >
                <span tw="opacity-75">
                  This note was last updated on
                  {differenceInDays(new Date(_updatedAt), new Date()) > 3
                    ? formatDistance(new Date(_updatedAt), new Date())
                    : format(new Date(_updatedAt), ' do MMM yyyy')}
                  {', '}
                </span>
                <div tw="flex flex-row items-center">
                  <span tw="opacity-75">by</span>
                  <GatsbyImage
                    tw="rounded-full w-8 h-8 overflow-hidden"
                    className="avatar"
                    css={{
                      margin: '0 !important',
                      marginLeft: '0.5rem !important',
                      marginRight: '0.5rem !important',
                      border: '2px solid transparent',
                      borderColor: `${theme`colors.accent`}`,
                      img: {
                        borderRadius: 9999,
                      },
                    }}
                    alt="Ajmal Afif"
                    image={props.childImageSharp.gatsbyImageData}
                  />
                  Ajmal Afif
                </div>
              </div>
            )}
          </div>
        )}
      </div>
      <section
        css={{
          width: 'min(1024px, calc(100% - 48px))',
          borderColor: `${theme`borderColor.accent`}`,
        }}
        tw="grid gap-4 grid-cols-1 md:grid-cols-2 max-w-full mx-auto border-t mt-12 md:mt-24 pt-5 md:pt-8 gap-x-8"
      >
        <div>
          {previous && (
            <div>
              <h4
                tw="font-sans pb-4"
                css={{
                  color: `${'var(--text-softer)'}`,
                  opacity: 0.5,
                }}
              >
                <span>← </span>Older note
              </h4>
              <Link to={getBlogUrl(props.previous.slug.current)} tw="w-full inline-flex">
                <div
                  key={previous.id}
                  className="card"
                  tw="w-full rounded-xl overflow-hidden inline-block border transition ease-in-out duration-150 hover:shadow-md hover:bg-gradient-to-t via-gray-200 scale-100 hover:scale-105 transform-gpu text-left"
                  css={{
                    borderColor: `${theme`borderColor.accent`}`,
                  }}
                >
                  <div tw="p-6">
                    <div tw="flex flex-col">
                      <h3 tw="prose text-2xl font-serif">{props.previous.title}</h3>
                    </div>
                    <h4 tw="prose text-lg mt-4 text-softer font-serif">
                      {toPlainText(previous._rawExcerpt)}
                    </h4>
                    <p
                      tw="prose prose-sm font-sans text-softer mt-4"
                      css={{
                        opacity: 0.6,
                      }}
                    >
                      Last updated on
                      {differenceInDays(new Date(_updatedAt), new Date()) > 3
                        ? formatDistance(new Date(_updatedAt), new Date())
                        : format(new Date(_updatedAt), ' do MMM yyyy')}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>

        <div>
          {next && (
            <div tw="md:text-right">
              <h4
                tw="font-sans pb-4"
                css={{
                  color: `${'var(--text-softer)'}`,
                  opacity: 0.5,
                }}
              >
                Newer note<span> →</span>
              </h4>
              <Link to={getBlogUrl(props.next.slug.current)} tw="w-full inline-flex">
                <div
                  key={next.id}
                  className="card"
                  tw="w-full rounded-xl overflow-hidden inline-block border transition ease-in-out duration-150 hover:shadow-md hover:bg-gradient-to-t via-gray-200 scale-100 hover:scale-105 transform-gpu text-left"
                  css={{
                    borderColor: `${theme`borderColor.accent`}`,
                  }}
                >
                  <div tw="p-6">
                    <div tw="flex flex-col">
                      <h3 tw="prose text-2xl font-serif">{props.next.title}</h3>
                    </div>
                    <h4 tw="prose text-lg mt-4 text-softer font-serif">
                      {toPlainText(next._rawExcerpt)}
                    </h4>
                    <p
                      tw="prose prose-sm font-sans text-softer mt-4"
                      css={{
                        opacity: 0.6,
                      }}
                    >
                      Last updated on
                      {differenceInDays(new Date(_updatedAt), new Date()) > 3
                        ? formatDistance(new Date(_updatedAt), new Date())
                        : format(new Date(_updatedAt), ' do MMM yyyy')}
                    </p>
                  </div>
                </div>
              </Link>
            </div>
          )}
        </div>
      </section>
    </article>
  )
}

export default BlogPost